import { useNavigate } from "react-router-dom";
import {
  Building2,
  Smartphone,
  CreditCard,
  Wallet,
  PiggyBank,
  TrendingUp,
  ChevronRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import type { AccountWithConvertedBalance } from '@/types/account-drilldown';
import { getDisplayName, disambiguateDisplayNames } from "@/lib/display-name";

export interface AccountListByTypeProps {
  accountsByType: Record<string, AccountWithConvertedBalance[]>;
  baseCurrency: string;
  selectedCurrency?: string;
  isLoading: boolean;
}

const accountTypeConfig: Record<
  string,
  { label: string; icon: typeof Building2 }
> = {
  bank: { label: "Bank Accounts", icon: Building2 },
  mobile_money: { label: "Mobile Money", icon: Smartphone },
  card: { label: "Cards", icon: CreditCard },
  cash: { label: "Cash", icon: Wallet },
  savings: { label: "Savings", icon: PiggyBank },
  investment: { label: "Investments", icon: TrendingUp },
};

export function AccountListByType({
  accountsByType,
  baseCurrency,
  selectedCurrency,
  isLoading,
}: AccountListByTypeProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const sortedTypes = Object.keys(accountsByType).sort();

  if (sortedTypes.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Wallet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No accounts found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {sortedTypes.map((type) => {
        const rawAccounts = accountsByType[type];
        const disambiguated = disambiguateDisplayNames(
          rawAccounts.map((a) => ({
            ...a,
            displayName: getDisplayName(a),
          }))
        );
        const accounts = disambiguated;
        const config = accountTypeConfig[type] || {
          label: type,
          icon: Wallet,
        };
        const Icon = config.icon;

        // Calculate total for this account type in selected currency
        const typeTotal = accounts.reduce(
          (sum, account) => sum + account.convertedBalance,
          0,
        );

        return (
          <Card key={type}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Icon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                {config.label}
                <Badge variant="secondary" className="ml-auto text-xs">
                  {accounts.length}
                </Badge>
              </CardTitle>
              {/* Display total for this account type */}
              <div className="text-sm text-muted-foreground">
                Total:{" "}
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: selectedCurrency,
                }).format(typeTotal)}
              </div>
            </CardHeader>
            <CardContent>
              <div
                className="space-y-2"
                role="list"
                aria-label={`${config.label} accounts`}>
                {accounts.map((account) => (
                  <button
                    key={account.id}
                    onClick={() => navigate(`/accounts/${account.id}`)}
                    className="w-full p-3 sm:p-4 rounded-lg border bg-card hover:bg-accent hover:text-accent-foreground transition-colors text-left group min-h-[44px] focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    aria-label={`View ${account.displayName} account details. Balance: ${new Intl.NumberFormat(
                      "en-US",
                      {
                        style: "currency",
                        currency: selectedCurrency,
                      },
                    ).format(account.convertedBalance)}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        {account.type == "bank" ? (
                          <>
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <p className="font-medium truncate text-sm sm:text-base">
                                {account.bank_name}
                              </p>
                              <span
                                className="text-muted-foreground hidden sm:inline"
                                aria-hidden="true">
                                •
                              </span>
                              <p className="font-medium truncate text-sm sm:text-base">
                                {account.account_number}
                              </p>
                              <Badge variant="outline" className="text-xs">
                                {account.type}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <p className="font-medium truncate text-sm sm:text-base">
                                {account.displayName}
                              </p>
                            </div>
                          </>
                        ) : (
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <p className="font-medium truncate text-sm sm:text-base">
                              {account.displayName}
                            </p>

                            <p className="font-medium truncate text-sm sm:text-base">
                              {account.account_number}
                            </p>
                            <Badge variant="outline" className="text-xs">
                              {account.type}
                            </Badge>
                          </div>
                        )}

                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                          {/* Display balance in selected currency */}
                          <div>
                            <p className="text-muted-foreground text-xs">
                              Balance
                            </p>
                            <p className="font-semibold break-all">
                              {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: selectedCurrency,
                              }).format(account.convertedBalance)}
                            </p>
                          </div>

                          {/* Show native currency if different from selected */}
                          {account.currency !== selectedCurrency && (
                            <>
                              <span
                                className="text-muted-foreground hidden sm:inline"
                                aria-hidden="true">
                                •
                              </span>
                              <div>
                                <p className="text-muted-foreground text-xs">
                                  Native: {account.currency}
                                </p>
                                <p className="font-semibold text-muted-foreground break-all">
                                  {new Intl.NumberFormat("en-US", {
                                    style: "currency",
                                    currency: account.currency,
                                  }).format(account.balance)}
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      <ChevronRight
                        className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0"
                        aria-hidden="true"
                      />
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
